/**
 * Program RAID Service
 * Risks, Assumptions, Issues, and Dependencies management
 */
import { apiClient } from './api'

export type RAIDType = 'risk' | 'assumption' | 'issue' | 'dependency'
export type RAIDStatus = 'open' | 'in_progress' | 'resolved' | 'closed'
export type Priority = 'low' | 'medium' | 'high' | 'critical'

export interface ProgramRAID {
  id: number
  program_id: number
  project_id?: number
  type: RAIDType  // Backend uses 'type' not 'raid_type'
  title: string
  description?: string
  owner_id?: number
  status: RAIDStatus
  priority: Priority
  due_date?: string
  resolution_date?: string
  impact?: string
  probability?: string
  mitigation_plan?: string
  metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface RAIDCreate {
  program_id: number
  project_id?: number
  type: RAIDType  // Backend uses 'type' not 'raid_type'
  title: string
  description?: string
  owner_id?: number
  status?: RAIDStatus
  priority: Priority
  impact?: string
  probability?: string
  mitigation_plan?: string
  resolution_date?: string
  metadata?: Record<string, unknown>
}

export interface RAIDUpdate {
  title?: string
  description?: string
  owner_id?: number
  status?: RAIDStatus
  priority?: Priority
  impact?: string
  probability?: string
  mitigation_plan?: string
  resolution_date?: string
  metadata?: Record<string, unknown>
}

export interface RAIDSummary {
  total: number
  by_type: Record<RAIDType, number>
  by_status: Record<RAIDStatus, number>
  by_priority: Record<Priority, number>
  critical_items: number
}

export const programRAIDService = {
  /**
   * List RAID items
   */
  async list(
    programId: number,
    raidType?: RAIDType,
    status?: RAIDStatus,
    priority?: Priority
  ): Promise<ProgramRAID[]> {
    const params: Record<string, unknown> = {}
    if (raidType) params.type = raidType
    if (status) params.status = status
    if (priority) params.priority = priority
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/raid`,
      { params }
    )
    return response.data
  },

  /**
   * Get RAID item
   */
  async get(programId: number, raidId: number): Promise<ProgramRAID> {
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/raid/${raidId}`
    )
    return response.data
  },

  /**
   * Create RAID item
   */
  async create(programId: number, data: RAIDCreate): Promise<ProgramRAID> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/raid`,
      data
    )
    return response.data
  },

  /**
   * Update RAID item
   */
  async update(
    programId: number,
    raidId: number,
    data: RAIDUpdate
  ): Promise<ProgramRAID> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/raid/${raidId}`,
      data
    )
    return response.data
  },

  /**
   * Delete RAID item
   */
  async delete(programId: number, raidId: number): Promise<void> {
    await apiClient.delete(
      `/api/v1/programs/${programId}/raid/${raidId}`
    )
  },

  /**
   * Resolve RAID item
   */
  async resolve(programId: number, raidId: number): Promise<ProgramRAID> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/raid/${raidId}/resolve`
    )
    return response.data
  },

  /**
   * Get RAID summary
   */
  async getSummary(programId: number): Promise<RAIDSummary> {
    const items = await this.list(programId)
    
    const summary: RAIDSummary = {
      total: items.length,
      by_type: {
        risk: 0,
        assumption: 0,
        issue: 0,
        dependency: 0,
      },
      by_status: {
        open: 0,
        in_progress: 0,
        resolved: 0,
        closed: 0,
      },
      by_priority: {
        low: 0,
        medium: 0,
        high: 0,
        critical: 0,
      },
      critical_items: 0,
    }

    items.forEach(item => {
      summary.by_type[item.type]++
      summary.by_status[item.status]++
      summary.by_priority[item.priority]++
      if (item.priority === 'critical' && item.status !== 'resolved' && item.status !== 'closed') {
        summary.critical_items++
      }
    })

    return summary
  },
}

